import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ResponseData } from '@freelancer/datastore';
import { Pwa } from '@freelancer/pwa';
import { Facebook as AppNativeFacebook } from '@laurentgoudet/ionic-native-facebook/ngx';
import { FACEBOOK_CONFIG } from './facebook.config';
import {
  FacebookAuthResponse,
  FacebookConfig,
  FacebookLoginResponse,
} from './facebook.interface';

export interface FacebookUser {
  readonly email: string;
  readonly name: string;
  readonly profileUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class Facebook {
  private readonly requiredPermissions = ['email', 'public_profile'];

  isEnabled = false;
  loadedPromise: Promise<boolean>;

  /**
   * An existing successful login state which we track
   * because the Facebook SDK complains if we call login multiple times
   */
  successfulLoginState: ResponseData<FacebookAuthResponse, fb.LoginStatus>;

  constructor(
    @Inject(FACEBOOK_CONFIG) private config: FacebookConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
    private pwa: Pwa,
    private appNativeFacebook: AppNativeFacebook,
  ) {
    if (this.config.appId && isPlatformBrowser(this.platformId)) {
      this.isEnabled = true;
    }
  }

  get appId() {
    return this.config.appId;
  }

  async getUserDetails(
    userAuthResponse: FacebookAuthResponse,
  ): Promise<FacebookUser | undefined> {
    if (!this.isEnabled) {
      return undefined;
    }

    await this.loadSdk();

    const userPromise = this.callAPI(
      `/${userAuthResponse.userID}?fields=first_name,email`,
    );
    const picPromise = this.callAPI(
      `/${userAuthResponse.userID}/picture?redirect=false`,
    );
    const userResponse = await userPromise;
    const picResponse = await picPromise;

    if (
      userResponse &&
      !userResponse.error &&
      userResponse.email &&
      userResponse.first_name
    ) {
      return {
        email: userResponse.email,
        name: userResponse.first_name,
        // don't block on not being able to fetch the profile pic
        profileUrl:
          picResponse && !picResponse.error ? picResponse.data.url : undefined,
      };
    }
  }

  async login(): Promise<ResponseData<FacebookAuthResponse, fb.LoginStatus>> {
    if (!this.isEnabled) {
      return {
        status: 'error',
        errorCode: 'unknown',
      };
    }

    if (this.successfulLoginState) {
      return this.successfulLoginState;
    }

    let fbResponse: FacebookLoginResponse;
    if (this.pwa.isNative()) {
      fbResponse = await this.appNativeFacebook
        .login(this.requiredPermissions)
        .then(response => ({
          status: response.status as fb.LoginStatus,
          authResponse: {
            accessToken: response.authResponse?.accessToken,
            userID: response.authResponse?.userID,
          },
        }))
        .catch(() => ({
          status: 'not_authorized',
        }));
    } else {
      fbResponse = await new Promise((resolve, reject) => {
        window.FB.login(
          (response: fb.StatusResponse) => {
            resolve(response);
          },
          {
            scope: this.requiredPermissions.join(','),
          },
        );
      });
    }

    if (fbResponse.status === 'connected' && fbResponse.authResponse) {
      this.successfulLoginState = {
        status: 'success',
        result: fbResponse.authResponse,
      };
      return this.successfulLoginState;
    }
    return {
      status: 'error',
      errorCode: fbResponse.status,
    };
  }

  loadSdk(): Promise<boolean> {
    if (this.loadedPromise) {
      return this.loadedPromise;
    }

    if (this.pwa.isNative()) {
      // native apps have the SDK built in and don't need to load the JS one
      // we can immediately fetch the login status
      this.loadedPromise = this.appNativeFacebook
        .getLoginStatus()
        .then(status => {
          if (status.status === 'connected') {
            this.successfulLoginState = {
              status: 'success',
              result: status.authResponse,
            };
          }
          // always resolve true
          return true;
        });
      return this.loadedPromise;
    }

    this.loadedPromise = new Promise((resolve, reject) => {
      const { appId } = this.config;
      if (!appId || !this.isEnabled) {
        resolve(false);
        return;
      }
      window.fbAsyncInit = () => {
        window.FB.init({
          appId,
          autoLogAppEvents: true,
          xfbml: false,
          cookie: false,
          status: true,
          version: 'v3.2',
        });
        // fetch the login status before resolving initialisation
        // this prevents popups from being blocked when we call `login` later.
        window.FB.getLoginStatus(status => {
          if (status.status === 'connected') {
            this.successfulLoginState = {
              status: 'success',
              result: status.authResponse,
            };
          }
          resolve(true);
        });
      };
      if (document.getElementById('external-fb-sdk')) {
        return;
      }
      const e = document.createElement('script');
      e.src = 'https://connect.facebook.net/en_US/sdk.js';
      e.id = 'external-fb-sdk';
      e.onerror = reject;
      const s = document.getElementsByTagName('script')[0];
      (s.parentNode as Node).insertBefore(e, s);
    });
    return this.loadedPromise;
  }

  /**
   * Make a facebook graph API call with the current auth state
   **/
  private async callAPI(endpoint: string): Promise<any> {
    const authResponse = this.successfulLoginState;
    if (authResponse.status === 'error') {
      return { error: true };
    }

    if (this.pwa.isNative()) {
      return this.appNativeFacebook
        .api(
          endpoint,
          [], // no extra permissions needed
        )
        .catch(() => ({ error: true }));
    }

    return new Promise<any>((resolve, reject) => {
      window.FB.api(
        endpoint,
        {
          redirect: false,
          access_token: authResponse.result.accessToken,
        },
        response => {
          resolve(response);
        },
      );
    });
  }
}
